import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Globe, Phone, Building2 } from 'lucide-react';
import { crmOrgsApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

export default function OrganizationList() {
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['crm-orgs', cid, search, page],
    queryFn: () => crmOrgsApi.list({ search, page, limit: 50 }),
    enabled: !!cid,
  });

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title="Organizations"
        intro="Accounts and companies you work with. Click a card to view contacts, deals and activity."
        actions={
          <Link to="/crm/organizations/new" className="btn btn-primary flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> New Organization
          </Link>
        }
      />

      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            className="input pl-9 w-full"
            placeholder="Search organizations…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="card h-36 animate-pulse bg-surface" />)}
        </div>
      ) : !data?.organizations.length ? (
        <div className="card">
          <EmptyState icon={<Building2 className="w-8 h-8" />}>
            No organizations found. Use <b>New Organization</b> to add one.
          </EmptyState>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.organizations.map(org => (
            <Link key={org.id} to={`/crm/organizations/${org.id}`} className="card hover:bg-row-odd transition-colors space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-fg">{org.name}</h3>
                  {org.industry && <p className="text-xs text-muted mt-0.5">{org.industry}</p>}
                </div>
                <Building2 className="w-5 h-5 text-muted" />
              </div>
              <div className="space-y-1 text-xs text-muted">
                {org.website && <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" />{org.website.replace(/^https?:\/\//, '')}</span>}
                {org.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{org.phone}</span>}
              </div>
              {org._count && (
                <div className="flex gap-3 text-xs text-muted border-t border-line pt-2">
                  <span>{org._count.contacts} contacts</span>
                  <span>{org._count.deals} deals</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {data && data.total > 50 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted">{(page - 1) * 50 + 1}–{Math.min(page * 50, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button className="btn btn-secondary" disabled={page * 50 >= data.total} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
